import { AffiliatePromotionLink } from '@/@types/AffiliatePromotionLink';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from '@/hooks/use-toast';
import {
  hasMultipleAffiliateLinkProducts,
  sortAffiliatePromotionLinks,
} from '@/utils/affiliatePromotionLinkHelpers';
import { Copy } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  links: AffiliatePromotionLink[];
  title?: string;
  showCloseButton?: boolean;
  emptyMessage?: string;
  copySuccessMessage?: string;
}

function getCheckoutLabel(link: AffiliatePromotionLink): string | undefined {
  const checkout = (link.label ?? link.checkoutName)?.trim();
  return checkout || undefined;
}

function getOfferPrice(link: AffiliatePromotionLink): string | undefined {
  return link.offerPriceFormatted ?? link.offerPrice;
}

export default function AffiliateLinksDialog({
  open,
  onOpenChange,
  links,
  title,
  showCloseButton = false,
  emptyMessage,
  copySuccessMessage,
}: Props) {
  const { t } = useTranslation('common');

  const sortedLinks = useMemo(() => sortAffiliatePromotionLinks(links), [links]);
  const hasMultipleProducts = useMemo(
    () => hasMultipleAffiliateLinkProducts(sortedLinks),
    [sortedLinks],
  );

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast({
        variant: 'success',
        description: copySuccessMessage ?? t('linkCopied'),
      });
    } catch {
      toast({
        variant: 'error',
        description: t('errorCopying'),
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title ?? t('yourLinks')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {sortedLinks.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">
              {emptyMessage ?? t('noCheckouts')}
            </p>
          ) : (
            sortedLinks.map((link) => {
              const offerName = link.offerName?.trim();
              const checkoutName = getCheckoutLabel(link);
              const offerPrice = getOfferPrice(link);
              const showCheckoutName = Boolean(
                checkoutName &&
                  checkoutName !== link.productName?.trim() &&
                  (hasMultipleProducts
                    ? !offerName || checkoutName !== offerName
                    : offerName && checkoutName !== offerName),
              );

              return (
                <div
                  key={link.id}
                  className="flex items-start justify-between gap-3 p-3 rounded-lg bg-zinc-900 border border-zinc-800"
                >
                  <div className="min-w-0 flex-1 space-y-1.5">
                    {hasMultipleProducts && link.productName?.trim() && (
                      <p className="text-sm font-medium text-white">
                        {t('affiliateLinks.product')}: {link.productName}
                      </p>
                    )}

                    {!hasMultipleProducts && (offerName || checkoutName) && (
                      <p className="text-sm font-medium text-white">
                        {offerName ?? checkoutName}
                      </p>
                    )}

                    {hasMultipleProducts && (offerName || offerPrice) && (
                      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                        {offerName && (
                          <span className="text-xs text-zinc-400">
                            {t('affiliateLinks.offer')}: {offerName}
                          </span>
                        )}
                        {offerPrice && (
                          <span className="text-xs font-semibold text-[#A9D342]">
                            {offerPrice}
                          </span>
                        )}
                      </div>
                    )}

                    {!hasMultipleProducts && offerPrice && (
                      <p className="text-xs font-semibold text-[#A9D342]">
                        {offerPrice}
                      </p>
                    )}

                    {showCheckoutName && (
                      <p className="text-xs text-zinc-500">
                        {t('affiliateLinks.checkout')}: {checkoutName}
                      </p>
                    )}

                    <p className="text-xs text-zinc-500 truncate">{link.url}</p>
                  </div>

                  <Button
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    onClick={() => handleCopyLink(link.url)}
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              );
            })
          )}
        </div>

        {showCloseButton && (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              {t('close')}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
